import type { CeremonyScreen } from './ceremonyScreen';

/**
 * **Copy dos estados terminais da cerimônia — fonte única e varrível.**
 *
 * Estes textos nasciam inline, dentro do `switch` de `SignPage.tsx`, e por isso escapavam de
 * todo gate de léxico: o de erro varre `describeError`, o de markup varre componentes
 * isolados, e nenhum dos dois monta a página inteira em cada um dos dezesseis estados. A
 * auditoria adversarial do M6 apontou o resultado — **sete ocorrências de "envelope" e uma de
 * "API" na tela do signatário**, escritas em texto corrido:
 *
 *   "Resposta vazia da API." · "signatário deste envelope" · "Envelope sem documento" ·
 *   "Este envelope não tem documento" · "Este envelope expirou" · "Peça um novo envelope" ·
 *   "Este envelope foi cancelado" · "Este envelope foi recusado"
 *
 * Mais "Seu stage ainda está bloqueado", com o termo em inglês onde o produto diz "etapa".
 *
 * Extrair para cá não é organização: é o que torna a varredura EXAUSTIVA possível. O gate
 * itera o mapa inteiro, e `Record` sobre os `kind` garante que um estado novo não entre sem
 * copy — nem sem passar pelo léxico.
 *
 * Os estados de fora são os que não têm texto fixo: `loading` e `load_error` (o segundo vem de
 * `describeError`), `signer_declined` (mostra o motivo declarado pela pessoa), `terminal_error`
 * (idem), `success` (o `SuccessPanel` decide), `simple` e `qualified` (são o fluxo, não um
 * desfecho).
 */
export type CopiedScreenKind = Exclude<
  CeremonyScreen['kind'],
  'loading' | 'load_error' | 'signer_declined' | 'terminal_error' | 'success' | 'simple' | 'qualified'
>;

export interface ScreenCopy {
  title: string;
  detail?: string;
}

export const SCREEN_COPY: Record<CopiedScreenKind, ScreenCopy> = {
  empty: {
    title: 'Nada para mostrar',
    // Era "Resposta vazia da API." — o signatário não sabe o que é uma API, e mesmo que
    // soubesse não teria o que fazer com a informação. O que ele pode fazer é recarregar.
    detail: 'Não veio nenhuma informação sobre este documento. Recarregue a página.',
  },
  signer_not_found: {
    title: 'Signatário não encontrado',
    detail: 'Este link não corresponde a nenhum participante deste envio.',
  },
  observer: {
    title: 'Participação como observador',
    detail: 'Observadores acompanham a cerimônia, mas não assinam o documento.',
  },
  not_started: {
    title: 'Ainda não é possível assinar',
    detail: 'O remetente ainda não iniciou esta cerimônia.',
  },
  envelope_expired: {
    title: 'O prazo deste envio terminou',
    detail: 'Peça um novo link a quem enviou o documento.',
  },
  envelope_cancelled: {
    title: 'Este envio foi cancelado',
    detail: 'Quem enviou o documento cancelou esta cerimônia.',
  },
  envelope_declined: {
    title: 'Este envio foi recusado',
    detail: 'A cerimônia terminou após a recusa de um participante.',
  },
  signer_expired: {
    title: 'Seu prazo para assinar expirou',
  },
  stage_blocked: {
    title: 'Aguardando participantes anteriores',
    // "stage" era o termo do BANCO aparecendo na tela; o produto diz "etapa" em todo lugar
    // (`STAGE_MODE_LABEL`, `ParticipantsTrail`), inclusive na trilha logo acima desta mesma
    // tela — um conceito com dois nomes, um deles em inglês.
    detail:
      'Sua etapa ainda não foi liberada. Esta página não tenta de novo sozinha; volte aqui quando os participantes anteriores concluírem.',
  },
};

/** Documento ausente num envio que deveria tê-lo — fora do mapa de `kind` porque não é um
 * estado da máquina de cerimônia, e sim uma lacuna de dado dentro do estado assinável. */
export const DOCUMENTO_AUSENTE: ScreenCopy = {
  title: 'Envio sem documento',
  detail: 'Este envio não tem documento para exibir.',
};

/** Já assinou, mas não há caminho de download nesta resposta. */
export const JA_ASSINADO: ScreenCopy = {
  title: 'Você já assinou este documento',
};
